import { Alert, Box, Container, TextField, Tooltip } from "@mui/material";
import { parseInt } from "lodash";
import { useEffect, useState } from "react";
import axios from "axios";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import {
  getInputLabelProps,
  getInputProps,
  PopperFullWidth,
} from "../../../common/appStyles";
import Combobox from "../../../components/common/Combobox";
import {
  addDaysToDate,
  GetDateInYYYYMMDDWithoutTimeFormat,
  MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION,
} from "../../../common/utils";
import EventResolutions from "./EventResolutions";
import ErrorMessage from "../../../components/common/ErrorMessage";
import AdditionalData from "./AdditionalData";
import { getUserData } from "../../../common/EntitiesManagement";
import ClientBranchSelector from "./ClientBranchSelector";
import ExpandableView from "../../../components/common/ExpandableView";
import { Add, Cancel } from "@mui/icons-material";
import CreatedByText from "../../../components/common/CreatedByText";

const AddEvent = () => {
  const {
    // endpoints
    PERSONAL_API,
    CLIENTS_API,
    EVENTTYPE_API,
    VEHICLE_ENDPOINT,
    PROCEEDING_ENDPOINT,

    // Models
    eventModel,

    // Data Objects
    locations,
    cities,

    // Global States Modifiers
    setCity,
    setEventModel,
    setCityFilterCode,
    getClientTypes,
    config,
  } = useGlobalContext();

  const [clientTypes, setClientTypes] = useState(null);

  const getclientTypesData = async () => {
    const clientT = await getClientTypes();
    setClientTypes(clientT);
  };

  const [states, setStates] = useState(null);
  const [citiesData, setCities] = useState(null);
  const [eventTypeData, setEventTypeData] = useState(null);
  const [clientsData, setClientsData] = useState(null);
  const [personalData, setPersonalData] = useState(null);
  const [vehicleData, setVehicleData] = useState(null);
  const [proceedingData, setProceedingData] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [clientBranches, setClientBranches] = useState([]);
  const [showClientBranchesModal, setShowClientBranchesModal] = useState(false);
  const maximumCharactersForDescription =
    MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION;
  const [descriptionCount, setDescriptionCount] = useState(
    eventModel?.Observation?.length ?? 0
  );
  const [showAddClient, setShowAddClient] = useState(false);
  const getClientBranches = async (clientId) => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(
      `${CLIENTS_API}/${clientId}/branch`,
      requestConfig
    );

    setClientBranches(data?.data?.Data);
  };

  const getProceedingData = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${PROCEEDING_ENDPOINT}/`, requestConfig);

    setProceedingData(data?.data);
  };

  const getVehicleData = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${VEHICLE_ENDPOINT}/assigned`, requestConfig);

    setVehicleData(data?.data);
  };

  const getPersonal = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${PERSONAL_API}/`, requestConfig);

    setPersonalData(data?.data);
  };

  const getClients = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${CLIENTS_API}/`, requestConfig);

    setClientsData(data?.data);
  };

  const getEventTypes = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${EVENTTYPE_API}/`, requestConfig);

    setEventTypeData(data?.data);
  };

  useEffect(() => {
    getPersonal();
    getClients();
    getEventTypes();
    getVehicleData();
    getProceedingData();
    getclientTypesData();
  }, []);

  const handleClientTypeChange = (selectedClientType) => {
    if (!selectedClientType) {
      setEventModel({
        ...eventModel,
        clientModel: {
          ...eventModel?.clientModel,
          clientTypeId: 0,
          ClientType: null,
        },
      });
      return;
    }

    setEventModel({
      ...eventModel,
      clientModel: {
        ...eventModel?.clientModel,
        clientTypeId: selectedClientType.Id,
        ClientType: selectedClientType,
      },
    });
  };

  useEffect(() => {
    if (clientBranches && clientBranches.length > 0) {
      setShowClientBranchesModal(true);
    }
  }, [clientBranches]);

  const handleChange = (e) => {
    if (e.target.name === "Observation") {
      setDescriptionCount(e.target.value.length);
    }

    setEventModel({ ...eventModel, [e.target.name]: e.target.value });
  };

  const handleExecutionValue = (e) => {
    if (e.target?.value?.length === 0) {
      setEventModel({
        ...eventModel,
        executionValue: 0,
        executionFinalValue: 0,
      });
      return;
    }

    const inputValue = parseInt(e.target.value);

    setEventModel({
      ...eventModel,
      executionValue: inputValue > 0 ? inputValue : 0,
      executionFinalValue: inputValue > 0 ? inputValue : 0,
    });
  };

  const handleSparePartsValue = (e) => {
    if (e.target?.value?.length === 0) {
      setEventModel({
        ...eventModel,
        sparePartsValue: 0,
        sparePartsFinalValue: 0,
      });
      return;
    }

    const inputValue = parseInt(e.target.value);

    setEventModel({
      ...eventModel,
      sparePartsValue: inputValue > 0 ? inputValue : 0,
      sparePartsFinalValue: inputValue > 0 ? inputValue : 0,
    });
  };

  const handleSparePartsRealValue = (e) => {
    if (e.target?.value?.length === 0) {
      setEventModel({
        ...eventModel,
        sparePartsRealValue: 0,
      });
      return;
    }

    const inputValue = parseInt(e.target.value);

    setEventModel({
      ...eventModel,
      sparePartsRealValue: inputValue > 0 ? inputValue : 0,
    });
  };

  const handleStateChange = (currentState) => {
    if (!currentState) {
      setEventModel({
        ...eventModel,
        StateId: 0,
        State: null,
        CityId: 0,
        City: null,
      });
      return;
    }

    setEventModel({
      ...eventModel,
      StateId: currentState.Id,
      State: currentState,
      CityId: 0,
      City: null,
    });
  };

  const handleCityChange = (currentCity) => {
    if (!currentCity) {
      setEventModel({
        ...eventModel,
        CityId: 0,
        City: null,
      });
      return;
    }

    setEventModel({
      ...eventModel,
      CityId: currentCity.Id,
      City: currentCity,
    });

    setCityFilterCode(currentCity.Code);
  };

  const handleEventTypeChange = (currentEventType) => {
    if (!currentEventType) {
      setEventModel({
        ...eventModel,
        EventTypeId: 0,
        EventType: undefined,
      });
      return;
    }

    setEventModel({
      ...eventModel,
      EventTypeId: currentEventType.Id,
      EventType: currentEventType,
      executionValue: currentEventType.executionValue,
      executionFinalValue: currentEventType.executionValue,
      ExpectedSolutionFinalDateValue: addDaysToDate(
        eventModel?.ExpectedSolutionDate,
        currentEventType?.AttentionDays ?? 0
      ),
      ExpectedSolutionFinalDate: GetDateInYYYYMMDDWithoutTimeFormat(
        addDaysToDate(
          eventModel?.ExpectedSolutionDate,
          currentEventType?.AttentionDays ?? 0
        )
      ),
    });
  };

  const handlePersonalAssignedChange = (currentPersonal) => {
    if (!currentPersonal) {
      setEventModel({
        ...eventModel,
        AssignedBy: null,
        PersonalAssigned: null,
        PersonalId: 0,
      });
      return;
    }

    setEventModel({
      ...eventModel,
      AssignedBy: getUserData()?.UserName,
      PersonalAssigned: currentPersonal,
      PersonalId: currentPersonal.id,
    });
  };

  const handleVehicleChange = (currentVehicle) => {
    if (!currentVehicle) {
      setEventModel({
        ...eventModel,
        VehicleAssigned: null,
        VehicleId: 0,
      });
      return;
    }

    setEventModel({
      ...eventModel,
      VehicleAssigned: currentVehicle,
      VehicleId: currentVehicle.Id,
    });
  };

  const handleClientChange = async (currentClient) => {
    if (currentClient) {
      setEventModel({
        ...eventModel,
        ClientId: currentClient.Id,
        Client: currentClient,
        Address: currentClient.Address,
        City: currentClient.City,
        State: currentClient.State,
        StateId: currentClient.State.Id,
        CityId: currentClient.City.Id,
        ReporterName: currentClient.ContactName
          ? currentClient.ContactName
          : currentClient.Name,
        ReporterEmail: currentClient.Email,
        ReporterPhoneNumber: currentClient.PhoneNumber,
        AddressComplement: currentClient.AddressComplement,
        ClientBranchId: 0,
      });

      getClientBranches(currentClient.Id);
    } else {
      setEventModel({
        ...eventModel,
        ClientId: 0,
        Client: null,
        ClientBranchId: 0,
      });
    }
  };

  const validateExpectedSolutionDates = (initial, final) => {
    if (initial > final) {
      setErrorMessage(localize.date_range_error);
      setShowErrorModal(true);
      return false;
    }

    return true;
  };

  const handleExpectedAttentionDateChange = (expectedSolutionDate) => {
    try {
      if (!expectedSolutionDate) {
        setEventModel({
          ...eventModel,
          ExpectedSolutionDateValue: null,
          ExpectedSolutionDate: null,
        });
        return;
      }

      if (
        validateExpectedSolutionDates(
          expectedSolutionDate,
          eventModel.ExpectedSolutionFinalDateValue
        )
      ) {
        setEventModel({
          ...eventModel,
          ExpectedSolutionDateValue: expectedSolutionDate,
          ExpectedSolutionDate:
            GetDateInYYYYMMDDWithoutTimeFormat(expectedSolutionDate),
        });
      }
    } catch {
      console.log("Invalid date");
    }
  };

  const handleExpectedAttentionFinalDateChange = (expectedSolutionDate) => {
    try {
      if (!expectedSolutionDate) {
        setEventModel({
          ...eventModel,
          ExpectedSolutionFinalDateValue: null,
          ExpectedSolutionFinalDate: null,
        });
        return;
      }

      if (
        validateExpectedSolutionDates(
          eventModel.ExpectedSolutionDateValue,
          expectedSolutionDate
        )
      ) {
        setEventModel({
          ...eventModel,
          ExpectedSolutionFinalDateValue: expectedSolutionDate,
          ExpectedSolutionFinalDate:
            GetDateInYYYYMMDDWithoutTimeFormat(expectedSolutionDate),
        });
      }
    } catch {
      console.log("Invalid date");
    }
  };

  const validateStateChange = () => {
    if (eventModel?.StateId > 0) {
      const selectedState = states?.find(
        (element) => element.Id === eventModel?.StateId
      );
      if (selectedState) {
        setCity(selectedState.Code);
      }
    }
  };

  useEffect(() => {
    if (cities && cities.length > 0) {
      if (cities && cities.length > 0 && eventModel?.City) {
        setCityFilterCode(eventModel.City.Code);
      }
    }
  }, [cities]);

  useEffect(() => {
    validateStateChange();
  }, [eventModel?.StateId, states]);

  useEffect(() => {
    if (locations.Ok && locations.Data.length > 0) {
      setStates(locations.Data);
    }
  }, [locations]);

  useEffect(() => {
    if (cities && cities.Ok && cities.Data.length > 0) {
      setCities(cities.Data);
    }
  }, [cities]);

  const handleProceedingChange = (e) => {
    if (!e) {
      setEventModel({
        ...eventModel,
        ProceedingInfo: null,
        ProceedingId: 0,
      });
      return;
    }

    setEventModel({
      ...eventModel,
      ProceedingInfo: e,
      ProceedingId: e.Id,
    });
  };

  const getBoxStyle = () => "w-full flex justify-between gap-2 items-center";

  const handleClientIdentificationTypeChange = (e) => {
    if (!e) {
      setEventModel({
        ...eventModel,
        clientModel: {
          ...eventModel?.clientModel,
          IdentificationTypeId: "Cédula de ciudadanía",
          IdentificationType: {
            Id: "Cédula de ciudadanía",
            Name: "Cédula de ciudadanía",
          },
        },
      });
      return;
    }

    setEventModel({
      ...eventModel,
      clientModel: {
        ...eventModel?.clientModel,
        IdentificationTypeId: e.Id,
        IdentificationType: { ...e },
      },
    });
  };

  const handleClientIdentificationChange = (e) => {
    setEventModel({
      ...eventModel,
      clientModel: {
        ...eventModel?.clientModel,
        Identification: e.target.value,
      },
    });
  };

  const getComponents = () => {
    const result = [
      {
        Name: localize.general_information,
        Component: (
          <Box className="w-full flex flex-col gap-3 mt-2">
            <Box className="flex gap-2 items-center">
              <TextField
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={eventModel.Code}
                onChange={handleChange}
                size="small"
                fullWidth
                id="Code"
                label={localize.code}
                name="Code"
                variant="outlined"
                autoComplete="off"
              />

              {eventModel?.Id <= 0 && (
                <Alert className="w-full" severity="info">
                  <label className="text-xs">
                    {localize.info_code_creation}
                  </label>
                </Alert>
              )}
            </Box>

            <Combobox
              id="Proceeding"
              fieldLabel={localize.proceeding}
              fieldOptions={proceedingData?.Data ?? []}
              fieldValue={eventModel?.ProceedingInfo}
              handleValueChange={handleProceedingChange}
              popperFullWidth={PopperFullWidth}
              customOptionLabel={(option) =>
                `${option?.Name ?? ""} ${
                  option?.Code ? `${localize.code}: (${option.Code})` : ""
                }`
              }
            />

            <Combobox
              isRequired
              id="EventTypeId"
              fieldLabel={localize.event_type}
              fieldOptions={eventTypeData?.Data ?? []}
              fieldValue={eventModel?.EventType}
              handleValueChange={handleEventTypeChange}
              popperFullWidth={PopperFullWidth}
            />

            <Box className="flex gap-2 w-full items-center">
              <Combobox
                disabled={showAddClient}
                id="Client"
                fieldLabel={localize.client}
                fieldOptions={clientsData?.Data ?? []}
                fieldValue={eventModel?.Client}
                handleValueChange={handleClientChange}
                popperFullWidth={PopperFullWidth}
                customOptionLabel={(option) =>
                  `${option.Identification} - ${option.Name}`
                }
              />

              {(!eventModel?.Id || eventModel?.Id === 0) && (
                <Tooltip
                  title={
                    !showAddClient
                      ? localize.add_client
                      : localize.cancel_client_creation
                  }
                >
                  <button
                    onClick={
                      !showAddClient
                        ? () => {
                            setEventModel({
                              ...eventModel,
                              ClientId: 0,
                              Client: null,
                              ClientBranchId: 0,
                            });
                            setShowAddClient(true);
                          }
                        : () => {
                            setEventModel({
                              ...eventModel,
                              clientModel: {},
                            });
                            setShowAddClient(false);
                          }
                    }
                  >
                    {showAddClient ? (
                      <Cancel
                        sx={{ color: "red", width: "24px", height: "24px" }}
                      />
                    ) : (
                      <Add
                        sx={{ color: "green", width: "24px", height: "24px" }}
                      />
                    )}
                  </button>
                </Tooltip>
              )}
            </Box>

            {showAddClient && (
              <Combobox
                isRequired
                id="ClientType"
                handleValueChange={handleClientTypeChange}
                fieldValue={eventModel?.clientModel?.ClientType}
                fieldOptions={clientTypes ?? []}
                fieldLabel={localize.client_type}
              />
            )}

            {showAddClient && (
              <Box className={getBoxStyle()}>
                <Combobox
                  isRequired
                  id="IdentificationType"
                  handleValueChange={handleClientIdentificationTypeChange}
                  fieldValue={
                    eventModel?.clientModel?.IdentificationType ?? null
                  }
                  fieldOptions={[
                    {
                      Id: "Cédula de ciudadanía",
                      Name: "Cédula de ciudadanía",
                    },
                    { Id: "Nit", Name: "Nit" },
                    { Id: "Pasaporte", Name: "Pasaporte" },
                    {
                      Id: "Cédula de extranjería",
                      Name: "Cédula de extranjería",
                    },
                    { Id: "Otro", Name: "Otro" },
                  ]}
                  fieldLabel={localize.identification_type}
                />

                <TextField
                  required
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  name="Identification"
                  value={eventModel?.clientModel?.Identification}
                  id="Identification"
                  label={localize.identification}
                  variant="outlined"
                  onChange={handleClientIdentificationChange}
                  autoComplete="off"
                />
              </Box>
            )}

            {showAddClient && (
              <TextField
                required
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                size="small"
                id="ReporterName"
                name="ReporterName"
                value={eventModel.ReporterName}
                onChange={handleChange}
                label={localize.client_name}
                variant="outlined"
                fullWidth
                autoComplete="off"
              />
            )}

            <Box className={getBoxStyle()}>
              <Combobox
                dontUseFullWidth
                isRequired
                id="State"
                fieldLabel={localize.department}
                fieldOptions={states ?? []}
                fieldValue={eventModel?.State}
                handleValueChange={handleStateChange}
                popperFullWidth={PopperFullWidth}
              />

              <Combobox
                dontUseFullWidth
                isRequired
                id="CityId"
                fieldLabel={localize.city}
                fieldOptions={eventModel?.State ? citiesData ?? [] : []}
                fieldValue={eventModel?.City}
                handleValueChange={handleCityChange}
                popperFullWidth={PopperFullWidth}
              />
            </Box>

            <TextField
              required
              InputLabelProps={getInputLabelProps()}
              inputProps={getInputProps()}
              size="small"
              value={eventModel.Address}
              onChange={handleChange}
              id="Address"
              label={localize.address}
              name="Address"
              variant="outlined"
              fullWidth
              autoComplete="off"
            />

            <TextField
              InputLabelProps={getInputLabelProps()}
              inputProps={getInputProps()}
              size="small"
              id="AddressComplement"
              name="AddressComplement"
              value={eventModel.AddressComplement}
              onChange={handleChange}
              label={localize.address_complement}
              variant="outlined"
              fullWidth
              autoComplete="off"
            />

            <Box className={getBoxStyle()}>
              <TextField
                required
                size="small"
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={eventModel.ReporterPhoneNumber}
                onChange={handleChange}
                fullWidth
                id="ReporterPhoneNumber"
                name="ReporterPhoneNumber"
                label={
                  showAddClient
                    ? localize.phone_number
                    : localize.reporter_phone_number
                }
                variant="outlined"
                autoComplete="off"
              />

              <TextField
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={eventModel.ReporterEmail}
                onChange={handleChange}
                size="small"
                id="ReporterEmail"
                name="ReporterEmail"
                label={showAddClient ? localize.email : localize.reporter_email}
                variant="outlined"
                fullWidth
                autoComplete="off"
              />
            </Box>

            {!showAddClient && (
              <TextField
                required
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                size="small"
                id="ReporterName"
                name="ReporterName"
                value={eventModel.ReporterName}
                onChange={handleChange}
                label={localize.reported_by}
                variant="outlined"
                fullWidth
                autoComplete="off"
              />
            )}

            <Box className={getBoxStyle()}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={`${localize.expected_attention_date} (${localize.start})`}
                  className="w-full"
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  PopperComponent={PopperFullWidth}
                  value={eventModel?.ExpectedSolutionDateValue}
                  onChange={handleExpectedAttentionDateChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "",
                      }}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        style: { fontSize: "1em", paddingBottom: "10px" },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={`${localize.expected_attention_date} (${localize.end})`}
                  className="w-full"
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  PopperComponent={PopperFullWidth}
                  value={eventModel?.ExpectedSolutionFinalDateValue}
                  onChange={handleExpectedAttentionFinalDateChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "",
                      }}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        style: {
                          fontSize: "1em",
                          paddingBottom: "10px",
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>

            <Combobox
              id="PersonalAIssigned"
              fieldLabel={localize.assigned_to}
              fieldOptions={personalData?.Data ?? []}
              fieldValue={eventModel?.PersonalAssigned}
              customOptionLabel={(option) =>
                `${option.Identification} - ${option.SurName} ${option.LastName}`
              }
              handleValueChange={handlePersonalAssignedChange}
              popperFullWidth={PopperFullWidth}
            />

            <Combobox
              id="vehicleAssigned"
              fieldLabel={localize.vehicle}
              fieldOptions={vehicleData?.Data ?? []}
              fieldValue={eventModel?.VehicleAssigned}
              customOptionLabel={(option) =>
                `(${option.VehicleType.Name}) ${option.Identification} - ${option.AssignedTo.SurName} ${option.AssignedTo.LastName}`
              }
              handleValueChange={handleVehicleChange}
              popperFullWidth={PopperFullWidth}
            />

            <Box className={getBoxStyle()}>
              <TextField
                type="number"
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={eventModel.executionFinalValue}
                onChange={handleExecutionValue}
                size="small"
                id="executionFinalValue"
                name="executionFinalValue"
                label={localize.service_value}
                variant="outlined"
                fullWidth
              />

              <TextField
                type="number"
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={eventModel.sparePartsFinalValue}
                onChange={handleSparePartsValue}
                size="small"
                id="sparePartsFinalValue"
                name="sparePartsFinalValue"
                label={localize.spare_parts_value}
                variant="outlined"
                fullWidth
              />
            </Box>

            <Box className={getBoxStyle()}>
              <TextField
                type="number"
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={eventModel.sparePartsRealValue}
                onChange={handleSparePartsRealValue}
                size="small"
                id="sparePartsRealValue"
                name="sparePartsRealValue"
                label={localize.spare_parts_real_value}
                variant="outlined"
                fullWidth
              />
            </Box>

            <Box className={getBoxStyle()}>
              <TextField
                InputLabelProps={getInputLabelProps()}
                inputProps={{
                  ...getInputProps(),
                  maxLength: maximumCharactersForDescription,
                }}
                size="small"
                fullWidth
                multiline
                rows={4}
                id="Observation"
                name="Observation"
                label={localize.observations}
                variant="outlined"
                onChange={handleChange}
                value={eventModel.Observation}
                helperText={`${descriptionCount}/${maximumCharactersForDescription}`}
                autoComplete="off"
              />
            </Box>
          </Box>
        ),
      },
    ];

    if (
      eventModel?.EventType?.AdditionalFields &&
      eventModel?.EventType?.AdditionalFields.length > 2
    ) {
      result.push({
        Name: localize.additional_data,
        Component: (
          <div className="w-full flex justify-center content-center items-center">
            <AdditionalData event={eventModel} setEvent={setEventModel} />
          </div>
        ),
      });
    }

    result.push({
      Name: localize.resolutions,
      Component: (
        <div className="w-full flex justify-center content-center items-center">
          <EventResolutions />
        </div>
      ),
    });

    return result;
  };

  return (
    <Container
      style={{ width: "100%", paddingLeft: "0px", paddingRight: "0px" }}
    >
      <div className="w-full">
        {showErrorModal && (
          <ErrorMessage
            message={errorMessage}
            title={localize.error}
            showModal={showErrorModal}
            setShowModal={setShowErrorModal}
          />
        )}

        {showClientBranchesModal && (
          <ClientBranchSelector
            show={showClientBranchesModal}
            setShow={setShowClientBranchesModal}
            eventModel={eventModel}
            setEventModel={setEventModel}
            client={eventModel?.Client}
            branches={clientBranches}
          />
        )}

        <ExpandableView components={getComponents()} />

        <CreatedByText entity={eventModel} />
      </div>
    </Container>
  );
};

export default AddEvent;
